import { action, observable, reaction } from 'mobx';
import { component, initialize, inject } from 'tsdi';

import { Api } from '../core/api';
import { ConnectApiDoubleOptInChannelsDto } from '../core/api/dtos';
import { TokenStore } from '../core/token-store';
import { UrlStore } from '../core/url-store';

export type MarketingConsentState =
    | 'FETCHING'
    | 'PENDING'
    | 'CONFIRMED'
    | 'DECLINED'
    | 'NOT_FOUND'
    | 'ERROR';

const INITIAL_STATE: MarketingConsentState = 'FETCHING';

@component
export class MarketingConsentStore {
    @inject
    private api!: Api;

    @inject
    private tokenStore!: TokenStore;

    @inject
    private urlStore!: UrlStore;

    @observable
    public state: MarketingConsentState = INITIAL_STATE;

    @observable
    public dto: ConnectApiDoubleOptInChannelsDto = {};

    @observable
    private requestUuid = '';

    public confirm = async () => {
        await this.request(
            this.api.client.ConnectApiDoubleOptInController
                .acceptDoubleOptInRequest,
            'CONFIRMED'
        );
    };

    public decline = async () => {
        await this.request(
            this.api.client.ConnectApiDoubleOptInController
                .declineDoubleOptInRequest,
            'DECLINED'
        );
    };

    @action
    private request = async (
        request: (
            customerId: number,
            query: { doubleOptInRequestUuid: string }
        ) => Promise<void>,
        successState: 'CONFIRMED' | 'DECLINED'
    ) => {
        try {
            this.state = 'FETCHING';

            await request(this.tokenStore.customerId, {
                doubleOptInRequestUuid: this.requestUuid
            });

            this.state = successState;
        } catch (e) {
            this.state = 'ERROR';
            console.warn((e as Error).message);
        }
    };

    @initialize
    public init = async () => {
        return reaction(
            () => this.urlStore.token,
            async (token) => {
                if (!token) {
                    return;
                }
                this.setUuidFromToken(token);
                await this.fetchPending();
                this.urlStore.setStudio(this.tokenStore.studioId.toString());
            },
            { fireImmediately: true }
        );
    };

    public setUuidFromToken = (token: string) => {
        try {
            [, , this.requestUuid] = atob(token).split(';');
        } catch (e) {
            this.state = 'ERROR';
            console.warn((e as Error).message);
        }
    };

    private fetchPending = async () => {
        this.state = 'FETCHING';

        if (!this.api.token) {
            this.state = 'ERROR';
            return;
        }

        try {
            this.dto =
                await this.api.client.ConnectApiDoubleOptInController.getDoubleOptInChannels(
                    this.tokenStore.customerId,
                    { doubleOptInRequestUuid: this.requestUuid }
                );

            const hasPendingConsents =
                this.state === 'FETCHING' &&
                (this.dto?.pendingConsents || []).length > 0;
            this.state = hasPendingConsents ? 'PENDING' : 'NOT_FOUND';
        } catch (e) {
            this.state = 'ERROR';
            console.warn((e as Error).message);
        }
    };
}
